import { Kopfleiste, Seitenkopf } from './Kopfleiste.js'
import { Fuss } from './Fuss.js'
import { useSeitenleben } from './bewegung.js'

/**
 * Die Markenseite.
 *
 * Nicht «über uns» und keine Gründergeschichte. Die Frage, die jemand hier
 * hat, ist eine andere: wofür steht das, und passt es zu meinem Betrieb.
 *
 * Aufbau wie die übrigen Seiten: Kopfleiste, Seitenkopf, dann eine Leiste mit
 * vier Merkmalen, «Unser Ansatz» mit fünf Punkten, «Warum es zählt» mit vier,
 * Herkunft, Aufruf, Fussbereich.
 *
 * Die Punkte sind Aussagen über das Produkt und nicht über Werte – «wir sind
 * ehrlich» kann jeder schreiben, «der Monteur sieht auf Wunsch keine Preise»
 * nicht.
 */

const LEISTE: Array<{ label: string; wert: string }> = [
  { label: 'Kein Papier', wert: 'Alles digital' },
  { label: 'Am Handy erfasst', wert: 'Auf der Baustelle' },
  { label: 'Schweizer Beleg', wert: 'Mit QR-Zahlteil' },
  { label: 'Ein Datensatz', wert: 'Drei Belege' }
]

const ANSATZ: Array<{ titel: string; text: string }> = [
  {
    titel: 'Ein Datensatz, drei Belege',
    text:
      'Was auf der Baustelle erfasst wird, trägt die Offerte weiter und die Rechnung ' +
      'rechnet es ab. Kein zweites Erfassen, keine Zettel, die abgetippt werden.'
  },
  {
    titel: 'Am Handy zuerst gedacht',
    text:
      'Der Rapport entsteht dort, wo die Arbeit passiert. Mit Handschuhen, bei schlechtem ' +
      'Empfang, in zwei Minuten – nicht abends am Küchentisch.'
  },
  {
    titel: 'Nichts, was es nicht gibt',
    text:
      'Keine Funktion auf der Seite, die in der Software fehlt. Wo Raporto etwas nicht ' +
      'tut – Buchhaltung, Lohn, Abschluss –, steht das da, statt umschrieben zu werden.'
  },
  {
    titel: 'Schweizer Belege',
    text:
      'QR-Zahlteil, Mehrwertsteuer mit den hiesigen Sätzen, Beträge in Franken, ' +
      'Handelsregisterabfrage beim Bund. Kein Werkzeug, das übersetzt wurde.'
  },
  {
    titel: 'Aus einem Betrieb entstanden',
    text:
      'Raporto ist aus der täglichen Arbeit von AV Parkett entstanden und wird dort ' +
      'eingesetzt. Jede Funktion musste sich zuerst dort bewähren.'
  }
]

const WARUM: Array<{ titel: string; text: string }> = [
  {
    titel: 'Der Abend gehört dir',
    text: 'Die Rechnung steht, wenn der Rapport steht – nicht drei Stunden später.'
  },
  {
    titel: 'Weniger Rückfragen',
    text: 'Was erfasst ist, steht auf dem Beleg. Der Kunde fragt nicht nach, was gemeint ist.'
  },
  {
    titel: 'Der Treuhänder bekommt Ordnung',
    text: 'Export als Datei, Belege als PDF, durchgehende Nummern ohne Lücken.'
  },
  {
    titel: 'Kein Werkzeug zu viel',
    text: 'Eine Anmeldung statt vier Programme, die sich gegenseitig nichts sagen.'
  }
]

export function Markenseite({
  angemeldet,
  aufStart,
  aufPfad,
  aufAnmelden,
  aufStarten
}: {
  angemeldet: boolean
  aufStart: () => void
  aufPfad: (pfad: string) => void
  aufAnmelden: () => void
  aufStarten: () => void
}): JSX.Element {
  const leben = useSeitenleben<HTMLDivElement>()

  return (
    <div ref={leben} className="rp-glasseite">
      <Kopfleiste
        angemeldet={angemeldet}
        aufStart={aufStart}
        aufPfad={aufPfad}
        aufAnmelden={aufAnmelden}
        aufStarten={aufStarten}
      />

      <Seitenkopf
        augenbraue="Über Raporto"
        titel="Administration,"
        hell="die mitarbeitet."
        vorspann={
          'Raporto ist für Betriebe gemacht, die ihre Arbeit draussen machen und die ' +
          'Verwaltung trotzdem im Griff haben wollen. Einmal erfassen. Nicht dreimal.'
        }
      />

      <main className="rp-glas-mitte">
        {/* Die Merkmalsleiste: vier Punkte in einer Zeile, direkt unter dem Kopf. */}
        <div className="rp-glas rp-mk-leiste">
          {LEISTE.map((e) => (
            <div className="rp-mk-leistenpunkt" key={e.label}>
              <span className="rp-mk-haken" aria-hidden="true" />
              <span>
                <small>{e.label}</small>
                <b>{e.wert}</b>
              </span>
            </div>
          ))}
        </div>

        <div className="rp-glas-reihe">
          <p className="rp-augenbraue-punkt">Unser Ansatz</p>
          <h2>Fünf Entscheidungen, die alles andere bestimmen.</h2>
        </div>

        <div className="rp-glas">
          <div className="rp-glas-punkte">
            {ANSATZ.map((e, i) => (
              <article className="rp-glas-punkt" key={e.titel}>
                <span className="rp-glas-zahl">{i < 9 ? `0${i + 1}` : i + 1}</span>
                <h3>{e.titel}</h3>
                <p>{e.text}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="rp-glas-reihe">
          <p className="rp-augenbraue-punkt">Warum es zählt</p>
          <h2>Was ein Betrieb davon hat.</h2>
        </div>

        <div className="rp-glas">
          <div className="rp-glas-punkte">
            {WARUM.map((e) => (
              <article className="rp-glas-punkt" key={e.titel}>
                <span className="rp-mk-haken" aria-hidden="true" />
                <h3>{e.titel}</h3>
                <p>{e.text}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="rp-glas-reihe">
          <p className="rp-augenbraue-punkt">Herkunft</p>
          <h2>Zürich, Schweiz.</h2>
        </div>

        <div className="rp-glas">
          <p>
            Die Datenbank steht in der Region Zürich, die Belege liegen bei einem Schweizer
            Anbieter. Wo genau was liegt und was sich technisch nicht ausschliessen lässt,
            steht namentlich in der Datenschutzerklärung – nicht als «Schweizer Qualität» auf
            einer Marketingseite.
          </p>
        </div>

        <div className="rp-glas rp-glas-aufruf">
          <p className="rp-augenbraue-punkt">Dreissig Tage</p>
          <h2>Dein Betrieb, deine Daten.</h2>
          <p>Kein Demokonto, keine Beispieldaten. Was du in der Testphase erfasst, bleibt.</p>
          <div className="rp-glas-knoepfe">
            <button
              type="button"
              className="rp-kl-start"
              id="btn-marke-pakete"
              onClick={() => aufPfad('/preise')}
            >
              Pakete ansehen
              <span aria-hidden="true">→</span>
            </button>
            <button
              type="button"
              className="rp-kl-anmelden"
              id="btn-marke-faq"
              onClick={() => aufPfad('/faq')}
            >
              Häufige Fragen
            </button>
          </div>
        </div>
      </main>

      <Fuss aufPfad={aufPfad} />
    </div>
  )
}
